import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Loading } from '@/components/shared/Loading/Loading';
import { NoResults } from '@/components/shared/NoResults/NoResults';
import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { ContentStatus, ExploreBlock } from '@addmile/backend-client';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { contentKeys } from '../../services/ContentManagerService';
import BlockCard from './BlockCard';
import BlockFilters, { GetBlockParams } from './BlockFilters';
import BlockPagination from './BlockPagination';

type BlocksPage = {
  blocks: ExploreBlock[];
  hasMore: boolean;
  nextCursor?: number;
};

interface BlockListProps {
  onEditBlock: (slug: string) => void;
  onDeleteBlock: (slug: string) => void;
  onCopyBlock: (slug: string) => void;
}

export const BlockList: React.FC<BlockListProps> = ({
  onEditBlock,
  onDeleteBlock,
  onCopyBlock,
}) => {
  const [page, setPage] = useState(0);

  const [filters, setFilters] = useState<GetBlockParams>({
    title: '',
    type: [],
    language: undefined,
    status: [],
    cursor: undefined,
    pageSize: 24,
  });

  const debouncedTitle = useDebounce(filters.title, 300);

  const queryFilters = useMemo(
    () => ({
      ...filters,
      title: debouncedTitle,
    }),
    [filters, debouncedTitle],
  );

  useEffect(() => {
    setPage(0);
  }, [queryFilters]);

  const fetchBlocks = useCallback(
    async ({ pageParam = 0 }: { pageParam?: number }) => {
      const token = await getAuthToken();
      const params: GetBlockParams = {
        ...queryFilters,
        cursor: pageParam,
        pageSize: 24,
      };

      const { data } = await hubApi.getExploreBlocks(
        params.title,
        params.type,
        params.status as ContentStatus[],
        params.language,
        params.cursor,
        params.pageSize,
        withAuthHeader(token),
      );

      const lightBlocks: ExploreBlock[] = data.blocks;

      const maxId = lightBlocks.reduce((max, block) => Math.max(max, block.id), pageParam);
      const nextCursor = data.hasMore ? maxId : undefined;

      return {
        blocks: lightBlocks,
        hasMore: data.hasMore,
        nextCursor,
      };
    },
    [queryFilters],
  );

  const { data, isLoading, isError, fetchNextPage, hasNextPage } = useInfiniteQuery<
    BlocksPage,
    Error,
    InfiniteData<BlocksPage>,
    ReturnType<typeof contentKeys.blocksList>,
    number
  >({
    queryKey: contentKeys.blocksList(queryFilters),
    queryFn: ({ pageParam = 0 }) => fetchBlocks({ pageParam }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
  });

  const pages = data?.pages ?? [];
  const currentPageBlocks: ExploreBlock[] = pages[page]?.blocks ?? [];
  const canGoNextPage = page < pages.length - 1 || !!hasNextPage;

  const handleFilterChange = (
    key: keyof GetBlockParams,
    value: GetBlockParams[keyof GetBlockParams],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleNextPage = async () => {
    const nextIndex = page + 1;
    // If we already have that page loaded, just move the pointer
    if (pages[nextIndex]) {
      setPage(nextIndex);
      return;
    }
    if (hasNextPage) {
      await fetchNextPage();
      setPage(nextIndex);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage((prev) => prev - 1);
    }
  };

  const content = useMemo(() => {
    if (isLoading) {
      return <Loading />;
    }
    if (isError) {
      return <NoResults />;
    }
    return currentPageBlocks.map((block) => (
      <BlockCard
        key={block.slug}
        block={block}
        onEditBlock={onEditBlock}
        onDeleteBlock={onDeleteBlock}
        onCopyBlock={onCopyBlock}
      />
    ));
  }, [isLoading, isError, currentPageBlocks, onEditBlock, onDeleteBlock, onCopyBlock]);

  return (
    <div className="grid gap-4">
      <BlockFilters filters={filters} onFilterChange={handleFilterChange} />

      <div className="flex flex-col gap-4">{content}</div>

      <BlockPagination
        currentPage={page}
        hasMore={canGoNextPage}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
      />
    </div>
  );
};

export default BlockList;
